import {getObject, getStringForDisplay} from 'Core/helpers/data';
import {cloneDeep, get} from 'lodash';
import {getTagsUsedInContent} from 'Tags/helpers';
import {TAG_LENGTH_PRECISION} from 'Tags/const';
import {SCHEDULE_INTERVAL_TYPE} from 'Pages/apps/default/projectPages/campaign/pages/ItemPage/const';
import {capitalize} from 'lodash/string';
import {translate, translatePath} from 'Core/i18n';
import {formatNumber, getAppNumberLocaleCode} from 'Core/helpers/number';
import {getDate, getDateString} from 'Core/helpers/datetime';
import {getAppDatePluginLocale} from 'Core/helpers/locale';
import {stringContainsTag} from 'Core/helpers/string';
import {replacement_fields_prefix, replacement_fields_suffix} from 'Config/app';

/**
 * Returns a massage definition popup tab title 
 * @param {CampaignItemDataObject} campaign - Campaign data object.
 * @return {`[${string}] ${string}`}
 */
export const getTabTitle = campaign => {
	return `[${getStringForDisplay(get(campaign, 'id'))}] ${getStringForDisplay(get(campaign, 'name'))}`;
}

/**
 * Check if message content has dynamic tags
 * @note Dynamic tags are tags that do not have precise length.
 *
 * @param {any} [content] - Content to check.
 * @param {TagDataObject[]} tags - All the tags supported in the message content.
 * @return {boolean}
 */
export const messageContentHasTags = (content, tags) => {
	if (!content || typeof content !== 'string') return false;

	const usedTags = getTagsUsedInContent(content, tags);
	
	return (usedTags.length > 0 && usedTags.some(tag => tag.lengthPrecision === TAG_LENGTH_PRECISION.AVERAGE));
}

/**
 * Check if message content has replacements
 * @note Replacements are tags that do not have precise length.
 *
 * @param {any} [content] - Content to check.
 * @param {string[]} replacements - All the replacements supported in the message content.
 * @return {boolean}
 */
export const messageContentHasReplacements = (content, replacements) => {
	if (!content || typeof content !== 'string') return false;

	return (
		Object.keys(getObject(replacements)).length > 0 &&
		stringContainsTag(content, replacement_fields_prefix, replacement_fields_suffix)
	);
}

/**
 * Check if message content has dynamic tags
 * @note Dynamic tags are tags that do not have precise length.
 * 
 * @param {any} [content] - Content to check.
 * @param {TagDataObject[]} tags - All the tags supported in the message content.
 * @param {string[]} replacements - All the replacements supported in the message content.
 * @return {boolean}
 */
export const isMessageContentDynamic = (content, tags, replacements) => {
	if (!content || typeof content !== 'string') return false;
	
	return (messageContentHasTags(content, tags) || messageContentHasReplacements(content, replacements));
};

/**
 * Get a schedule interval description based on interval type and days
 * 
 * @param {ScheduleIntervalType} intervalType
 * @param {string[]} [days]
 * @param {string} [time]
 * @param {string} [timeZoneId]
 * @param {boolean} [timeInNewLine]
 * @return {string}
 */
export const getScheduleIntervalDescription = (intervalType, days, time, timeZoneId, timeInNewLine) => {
	// Translate path
	let result = '';

	switch (intervalType) {
		case SCHEDULE_INTERVAL_TYPE.DATE_OF_WEEK:
			if (!Array.isArray(days) || !days.length) {
				result += `—`;
			} else if (days.length === 1) {
				result += ` ${capitalize(translatePath(`constants.schedule.interval_days.${days[0]}.prefix`))}`;
				result += ` ${translatePath(`constants.schedule.interval_days.${days[0]}.label`)}`;
			} else if (days.length > 1) {
				let scheduleWeekDaysWithoutLast = cloneDeep(days);
				const lastWeekDay = scheduleWeekDaysWithoutLast.pop();
				scheduleWeekDaysWithoutLast.forEach((d, idx) => {
					if (idx === 0) {
						result += ` ${capitalize(translatePath(`constants.schedule.interval_days.${d}.prefix`))}`;
					}
					result += ` ${translatePath(`constants.schedule.interval_days.${d}.label`)}`;
					if (idx < scheduleWeekDaysWithoutLast.length - 1) result += `,`;
				});
				result += ` ${translate('AND', 'constants')}`;
				result += ` ${translatePath(`constants.schedule.interval_days.${lastWeekDay}.label`)}`;
			}
			break;

		case SCHEDULE_INTERVAL_TYPE.DATE_OF_MONTH:
			if (!Array.isArray(days) || !days.length) {
				result += `—`;
			} else {
				result += ` ${capitalize(translatePath('constants.schedule.every'))}`;
				if (days.length === 1) {
					result += ` ${formatNumber(days[0], '0o', getAppNumberLocaleCode())}`;
				} else if (days.length > 1) {
					let scheduleMonthDaysWithoutLast = cloneDeep(days);
					const lastMothDay = scheduleMonthDaysWithoutLast.pop();
					scheduleMonthDaysWithoutLast.forEach((d, idx) => {
						result += ` ${formatNumber(d, '0o', getAppNumberLocaleCode())}`;
						if (idx < scheduleMonthDaysWithoutLast.length - 1) result += `,`;
					});
					result += ` ${translate('AND', 'constants')}`;
					result += ` ${formatNumber(lastMothDay, '0o', getAppNumberLocaleCode())}`;
				}
				result += ` ${translatePath('constants.schedule.monthly_suffix')}`;
			}
			break;

		case SCHEDULE_INTERVAL_TYPE.DATE_OF_YEAR:
			if (!Array.isArray(days) || !days.length) {
				result += `—`;
			} else {
				result += ` ${capitalize(translatePath('constants.schedule.every'))}`;
				if (days.length === 1) {
					result += ` ${getDateString(
						getDate(days[0], 'M-d'),
						'do MMM',
						getAppDatePluginLocale()
					)}`;
				} else if (days.length > 1) {
					let scheduleYearDaysWithoutLast = cloneDeep(days);
					const lastYearDay = scheduleYearDaysWithoutLast.pop();
					scheduleYearDaysWithoutLast.forEach((d, idx) => {
						result += ` ${getDateString(
							getDate(d, 'M-d'),
							'do MMM',
							getAppDatePluginLocale()
						)}`;
						if (idx < scheduleYearDaysWithoutLast.length - 1) result += `,`;
					});
					result += ` ${translate('AND', 'constants')}`;
					result += ` ${getDateString(
						getDate(lastYearDay, 'M-d'),
						'do MMM',
						getAppDatePluginLocale()
					)}`;
				}
				result += ` ${translatePath('constants.schedule.yearly_suffix')}`;
			}
			break;

		// no default
	}

	if (!!time) {
		result += `${timeInNewLine ? `<br />` : ' '}${translatePath('constants.schedule.at')} ${time}`;
		result += translatePath('constants.schedule.timeSuffix');
		if (timeZoneId) result += ` (${timeZoneId})`;
	}

	return result;
};